/**
 * Pure helpers for walking an error's `.cause` chain. Kept dependency-free so
 * they can be imported by lightweight utilities without dragging in the wallet
 * / wagmi graph (which has module-load side effects).
 */

/**
 * Walk an error's `.cause` chain and return each link. Viem nests the real
 * error deeper in the chain (TransactionExecutionError → InternalRpcError → …).
 */
export function collectErrorChain(err: unknown): unknown[] {
  const chain: unknown[] = [];
  let current: unknown = err;
  const visited = new WeakSet<object>();

  while (current != null) {
    if (typeof current === 'object' && current !== null) {
      if (visited.has(current)) break;
      visited.add(current);
    }
    chain.push(current);

    current =
      current instanceof Error
        ? current.cause
        : typeof current === 'object' && current !== null && 'cause' in current
          ? (current as { cause: unknown }).cause
          : undefined;
  }

  return chain;
}

/**
 * Collects all messages from an error's cause chain.
 * Viem errors (TransactionExecutionError → InternalRpcError → Error) nest the
 * real context in `.cause`, `.details`, and `.shortMessage`.
 */
export function collectErrorMessages(err: unknown): string[] {
  const messages: string[] = [];

  for (const current of collectErrorChain(err)) {
    if (current instanceof Error) {
      messages.push(current.message);
    } else if (typeof current === 'object' && current !== null && 'message' in current) {
      messages.push(String((current as { message: unknown }).message));
    }

    // Viem BaseError exposes `.details` and `.shortMessage` as top-level strings
    if (typeof current === 'object' && current !== null) {
      const obj = current as Record<string, unknown>;
      if (typeof obj.details === 'string') messages.push(obj.details);
      if (typeof obj.shortMessage === 'string') messages.push(obj.shortMessage);
    }
  }

  return messages;
}
